import { getObjectId } from '@/helpers/object.id.helper';
import { isLiveSegmentFullyPreloaded, isLiveSegmentKindActive } from '@/helpers/flow-control-segment-active.helper';
import type { FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import type { SlotSegmentAnchor } from './slot-right-section-play-icon';

export type FlowControlSegmentSimulationState = {
  rows: SimulationCueSheetRow[];
  pointer: number;
  selectedItemIds: string[];
  activeSegmentKind: FlowControlSegmentKind | null;
  activeSegmentSlotId?: string;
};

function resolveSlotId(slotId?: string): string | undefined {
  return slotId ? (getObjectId(slotId) ?? slotId) : undefined;
}

export function computeFlowControlSegmentActive(
  segmentAnchor: SlotSegmentAnchor,
  segmentKind: FlowControlSegmentKind | undefined,
  slotId: string | undefined,
  options: {
    override?: boolean;
    isSimulation: boolean;
    cueState: FlowCueStateV1 | undefined;
    simulation: FlowControlSegmentSimulationState;
  },
): boolean {
  if (options.override === true) {
    return true;
  }

  if (!segmentKind) {
    return false;
  }

  const resolvedSlotId = resolveSlotId(slotId);

  if (options.isSimulation) {
    const { rows, pointer, selectedItemIds, activeSegmentKind, activeSegmentSlotId } = options.simulation;
    if (selectedItemIds.length === 0) {
      return false;
    }
    const pointerRow = rows[pointer];
    const effectiveKind = pointerRow?.segmentKind ?? activeSegmentKind;
    if (effectiveKind !== segmentKind) {
      return false;
    }
    if (segmentAnchor === 'slot') {
      const pointerSlotId = pointerRow?.slotId ? resolveSlotId(pointerRow.slotId) : undefined;
      const effectiveSlotId = pointerSlotId ?? activeSegmentSlotId;
      if (effectiveSlotId && resolvedSlotId) {
        const normalizedEffective = resolveSlotId(effectiveSlotId);
        if (normalizedEffective !== resolvedSlotId) {
          return false;
        }
      }
    }
    return true;
  }

  return isLiveSegmentKindActive(options.cueState, segmentKind, segmentAnchor, resolvedSlotId);
}

export function computeFlowControlSegmentPreloaded(
  segmentKind: FlowControlSegmentKind | undefined,
  slotId: string | undefined,
  options: {
    hasAutoCue: boolean;
    liveRunning: boolean;
    cueState: FlowCueStateV1 | undefined;
    skippedItemIds?: readonly string[];
  },
): boolean {
  if (!options.hasAutoCue || !options.liveRunning || !segmentKind) {
    return false;
  }

  return isLiveSegmentFullyPreloaded(options.cueState, segmentKind, resolveSlotId(slotId), options.skippedItemIds);
}
